import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dropdown, Input, Menu, PageHeader, Modal } from 'antd';

import * as S from './UserList.styles';
import { generatePath, RouteComponentProps } from 'react-router';
import { RouteConstants } from 'app/routes';
import { selectSelectedEnv } from 'app/containers/HiikoEnv/slice/selectors';
import { useUserSlice } from '../../slice';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { selectUserListState } from '../../slice/selectors';
import {
  IUserListItem,
  PermissionsEnum,
  userListTableColEnum,
} from '../../slice/types';
import AddUserForm from '../../parts/AddUserForm/AddUserForm';
import { ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { selectUser } from 'app/containers/Authorization/slice/selectors';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import { StyledTable } from 'app/components/StyledTable/StyledTable';

interface IProps extends RouteComponentProps {}

const UserList = ({ history }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { actions } = useUserSlice();

  const selectedEnv = useSelector(selectSelectedEnv);
  const selectedCompanyId = useSelector(selectSelectedCompanyId);
  const { loading, list } = useSelector(selectUserListState);
  const loggedInUser = useSelector(selectUser);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (selectedEnv) {
      dispatch(
        actions.getUserList({
          envId: selectedEnv.id,
          companyId: selectedCompanyId,
        }),
      );
    }
  }, [dispatch, actions, selectedEnv, selectedCompanyId]);

  const openUserDetails = (userId: number) => () => {
    if (selectedEnv) {
      history.push(
        generatePath(RouteConstants.user.details, {
          envId: Number(selectedEnv.id),
          userId,
        }),
      );
    }
  };

  const openConfimDeleteModal = useCallback(
    (user: IUserListItem) => {
      Modal.confirm({
        title: t('User.List.DeleteUserModal.AreYouSure', {
          defaultValue:
            'Are you sure you want to remove {{email}} from to the {{envName}} environment?',
          email: user.email || '',
          envName: selectedEnv?.name || '',
        }),
        icon: <ExclamationCircleOutlined />,

        cancelText: t('Task.Details.Cancel', 'Cancel'),

        okText: t('Task.Details.Confirm', 'Confirm'),

        onOk() {
          if (selectedEnv) {
            dispatch(
              actions.submitRemoveUserFromEnv({
                envId: selectedEnv.id,
                userId: user.id,
              }),
            );
          }
        },
      });
    },
    [t, selectedEnv, dispatch, actions],
  );

  const columns = useMemo(
    () => [
      {
        title: t('User.List.Table.Name', 'Name'),
        dataIndex: 'name',
        key: 'name',
        render: (_: string, user: IUserListItem) =>
          user.name && user.lastName
            ? `${user.name} ${user.lastName}`
            : t('User.Details.UserInvited', 'Invited user'),
        width: '25%',
        sorter: {
          compare: (a: IUserListItem, b: IUserListItem) => {
            return a.name?.localeCompare(b?.name);
          },
        },
      },
      {
        title: t('User.List.Table.Email', 'Email'),
        dataIndex: userListTableColEnum.email,
        key: userListTableColEnum.email,
        width: '35%',
        sorter: {
          compare: (a: IUserListItem, b: IUserListItem) => {
            return a.email.localeCompare(b.email);
          },
        },
      },

      {
        title: t('Common.Actions', 'Actions'),
        dataIndex: userListTableColEnum.actions,
        key: userListTableColEnum.actions,
        render: (_: unknown, user: IUserListItem) => (
          <div onClick={e => e.stopPropagation()}>
            <Dropdown
              trigger={['click']}
              disabled={user.email === loggedInUser?.email}
              overlay={
                <PermissionsWrapper
                  requiredEditPermissions={[
                    PermissionsEnum.usersAdministration,
                  ]}
                >
                  <Menu>
                    <Menu.Item
                      onClick={() => {
                        dispatch(
                          actions.toggleAddUserForm({
                            userEmail: user.email,
                            type: 'edit',
                            isOpen: true,
                            permissions: user.permissions,
                          }),
                        );
                      }}
                    >
                      {t(
                        'User.List.Table.ManagePermissions',
                        'Manage permissions',
                      )}
                    </Menu.Item>

                    <Menu.Item onClick={() => openConfimDeleteModal(user)}>
                      {t('User.List.Table.DeleteUser', 'Delete user')}
                    </Menu.Item>
                  </Menu>
                </PermissionsWrapper>
              }
            >
              <EllipsisIcon />
            </Dropdown>
          </div>
        ),
        width: '20%',
      },
    ],
    [t, openConfimDeleteModal, actions, dispatch, loggedInUser],
  );

  const listWithSearchApplied = useMemo(() => {
    const searchedList = list.map(user => {
      const query = searchQuery?.toLowerCase().trim();

      if (!query.length) {
        return { ...user, isVisible: true };
      }

      const keywordsArr = query.split(' ');

      const isVisible = Boolean(
        keywordsArr.find(
          k =>
            user.name?.toLowerCase().includes(k) ||
            user.lastName?.toLowerCase().includes(k) ||
            user.email?.toLowerCase().includes(k),
        ),
      );

      return { ...user, isVisible };
    });
    return searchedList.filter(user => user.isVisible);
  }, [list, searchQuery]);

  if (!selectedEnv) {
    return null;
  }

  return (
    <S.UserListWrapper>
      <PageHeader
        className="page-header"
        title={t('User.List.Title', 'Users')}
        extra={[
          <PermissionsWrapper
            requiredEditPermissions={[PermissionsEnum.usersAdministration]}
          >
            <Button
              key="0"
              type="primary"
              onClick={() =>
                dispatch(
                  actions.toggleAddUserForm({ isOpen: true, type: 'add' }),
                )
              }
            >
              {t('User.List.AddUser', 'Add user')}
            </Button>
          </PermissionsWrapper>,
        ]}
      />
      <S.ContentWrapper>
        <Input
          prefix={<SearchOutlined />}
          style={{ width: 298, marginBottom: 28 }}
          placeholder={t('User.List.SearchUsersPlaceholder', 'Search users')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.currentTarget.value)}
        />
        <StyledTable
          loading={loading}
          rowKey="id"
          dataSource={listWithSearchApplied}
          columns={columns}
          pagination={false}
          onRow={({ id }) => {
            return {
              onClick: openUserDetails(id),
            };
          }}
        />
      </S.ContentWrapper>
      <AddUserForm />
    </S.UserListWrapper>
  );
};

export default UserList;
